// Networking lessons - 30 unique lessons covering curl, netcat, and network diagnostics

const networkingLessons = [
  // BEGINNER LESSONS (10)
  {
    id: 'net-001',
    title: 'Basic curl Request',
    description: 'curl is a command-line tool to transfer data from or to a server. Fetch a webpage.',
    task: 'Use curl to fetch the content of https://example.com.',
    hint: 'Simply use curl followed by the URL.',
    example: 'curl https://google.com',
    answer: ['curl https://example.com', 'curl http://example.com'],
    tips: [
      'curl outputs the response body to stdout by default',
      'https:// is used for secure connections'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'curl',
      description: 'Transfer data from or to a server using various protocols (HTTP, HTTPS, FTP, etc.)',
      syntax: 'curl [options] <URL>',
      options: [
        { flag: '-o <file>', description: 'Write output to file instead of stdout' },
        { flag: '-O', description: 'Write output to file named like the remote file' },
        { flag: '-I', description: 'Fetch HTTP headers only (HEAD request)' },
        { flag: '-v', description: 'Verbose mode - show connection details' },
        { flag: '-s', description: 'Silent mode - hide progress meter' }
      ],
      examples: [
        { code: 'curl https://example.com', description: 'Fetch webpage content' },
        { code: 'curl -s https://api.example.com/data', description: 'Silently fetch API data' }
      ]
    }
  },
  {
    id: 'net-002',
    title: 'Save curl Output',
    description: 'Save the response from a URL to a local file instead of displaying it.',
    task: 'Download https://example.com and save it to a file called "page.html".',
    hint: 'Use -o to specify the output filename.',
    example: 'curl -o output.txt https://somesite.com',
    answer: ['curl -o page.html https://example.com'],
    tips: [
      '-o specifies the output filename',
      '-O uses the remote filename automatically'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'curl',
      description: 'Save downloaded content to a file using output options',
      syntax: 'curl -o <filename> <URL>',
      options: [
        { flag: '-o <file>', description: 'Save output to specified filename' },
        { flag: '-O', description: 'Save using the remote filename' },
        { flag: '-C -', description: 'Resume a previous download' },
        { flag: '--create-dirs', description: 'Create necessary directories for output path' }
      ],
      examples: [
        { code: 'curl -o page.html https://example.com', description: 'Save to specific filename' },
        { code: 'curl -O https://example.com/file.zip', description: 'Save with original filename' },
        { code: 'curl -C - -O https://example.com/large.zip', description: 'Resume interrupted download' }
      ]
    }
  },
  {
    id: 'net-003',
    title: 'View Response Headers',
    description: 'Fetch only the HTTP headers without the body content.',
    task: 'Get only the HTTP headers from https://example.com.',
    hint: 'Use -I flag to fetch headers only.',
    example: 'curl -I https://google.com',
    answer: ['curl -I https://example.com'],
    tips: [
      '-I performs a HEAD request (headers only)',
      '-i includes headers WITH the body'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'curl',
      description: 'View HTTP response headers to inspect server responses and metadata',
      syntax: 'curl -I <URL>',
      options: [
        { flag: '-I', description: 'Fetch headers only (HEAD request)' },
        { flag: '-i', description: 'Include headers in output with body' },
        { flag: '-D <file>', description: 'Save headers to a file' },
        { flag: '-v', description: 'Show request and response headers (verbose)' }
      ],
      examples: [
        { code: 'curl -I https://example.com', description: 'Get headers only' },
        { code: 'curl -i https://example.com', description: 'Get headers plus body' },
        { code: 'curl -D headers.txt https://example.com', description: 'Save headers to file' }
      ]
    }
  },
  {
    id: 'net-004',
    title: 'Test Connectivity with ping',
    description: 'ping sends ICMP echo requests to test if a host is reachable.',
    task: 'Ping google.com to test connectivity.',
    hint: 'Use ping followed by the hostname.',
    example: 'ping localhost',
    answer: ['ping google.com'],
    tips: [
      'Press Ctrl+C to stop continuous pinging',
      'ping shows round-trip time (RTT) in milliseconds'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'ping',
      description: 'Send ICMP echo requests to test network connectivity to a host',
      syntax: 'ping [options] <host>',
      options: [
        { flag: '-c <count>', description: 'Stop after sending specified number of packets' },
        { flag: '-i <interval>', description: 'Seconds between sending each packet' },
        { flag: '-W <timeout>', description: 'Time to wait for response in seconds' },
        { flag: '-q', description: 'Quiet output - only show summary' },
        { flag: '-s <size>', description: 'Specify packet size in bytes' }
      ],
      examples: [
        { code: 'ping google.com', description: 'Ping host continuously' },
        { code: 'ping -c 4 google.com', description: 'Send exactly 4 packets' },
        { code: 'ping -i 2 google.com', description: 'Ping every 2 seconds' }
      ]
    }
  },
  {
    id: 'net-005',
    title: 'Limited ping',
    description: 'Send a specific number of ping requests instead of continuous pinging.',
    task: 'Send exactly 5 ping requests to google.com.',
    hint: 'Use -c flag to specify count.',
    example: 'ping -c 3 localhost',
    answer: ['ping -c 5 google.com'],
    tips: [
      '-c sets the count of packets to send',
      'Useful for scripts and quick connectivity tests'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'ping',
      description: 'Send a limited number of ping requests for quick connectivity tests',
      syntax: 'ping -c <count> <host>',
      options: [
        { flag: '-c <count>', description: 'Number of packets to send' },
        { flag: '-w <deadline>', description: 'Maximum time to wait regardless of packets' },
        { flag: '-q', description: 'Quiet mode - only show final statistics' },
        { flag: '-4', description: 'Use IPv4 only' },
        { flag: '-6', description: 'Use IPv6 only' }
      ],
      examples: [
        { code: 'ping -c 5 google.com', description: 'Send 5 ping requests' },
        { code: 'ping -c 3 -q google.com', description: 'Quick test with summary only' },
        { code: 'ping -c 10 -w 5 host.com', description: 'Send up to 10 packets within 5 seconds' }
      ]
    }
  },
  {
    id: 'net-006',
    title: 'Display Hostname',
    description: 'Show the hostname of your current machine.',
    task: 'Display the hostname of your system.',
    hint: 'Use the hostname command.',
    example: 'whoami',
    answer: ['hostname'],
    tips: [
      'hostname -f shows the fully qualified domain name (FQDN)',
      'hostname -I shows all IP addresses'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'hostname',
      description: 'Display or set the system hostname',
      syntax: 'hostname [options]',
      options: [
        { flag: '-f', description: 'Display the fully qualified domain name (FQDN)' },
        { flag: '-I', description: 'Display all network addresses of the host' },
        { flag: '-i', description: 'Display the IP address of the hostname' },
        { flag: '-s', description: 'Display short hostname (up to first dot)' },
        { flag: '-d', description: 'Display the DNS domain name' }
      ],
      examples: [
        { code: 'hostname', description: 'Show current hostname' },
        { code: 'hostname -f', description: 'Show fully qualified domain name' },
        { code: 'hostname -I', description: 'Show all IP addresses' }
      ]
    }
  },
  {
    id: 'net-007',
    title: 'View Network Interfaces',
    description: 'Display information about network interfaces and their IP addresses.',
    task: 'Show all network interfaces and their IP addresses using ip command.',
    hint: 'Use ip addr or ip a for short.',
    example: 'ifconfig',
    answer: ['ip addr', 'ip a', 'ip address'],
    tips: [
      'ip addr show is the modern replacement for ifconfig',
      'Look for inet for IPv4 and inet6 for IPv6 addresses'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'ip addr',
      description: 'Display IP addresses and network interface information',
      syntax: 'ip addr [show] [dev <interface>]',
      options: [
        { flag: 'show', description: 'Display addresses (default action)' },
        { flag: 'dev <interface>', description: 'Show only specific interface' },
        { flag: '-4', description: 'Show only IPv4 addresses' },
        { flag: '-6', description: 'Show only IPv6 addresses' },
        { flag: '-br', description: 'Brief output format' }
      ],
      examples: [
        { code: 'ip addr', description: 'Show all interfaces and addresses' },
        { code: 'ip -4 addr', description: 'Show only IPv4 addresses' },
        { code: 'ip addr show dev eth0', description: 'Show specific interface' },
        { code: 'ip -br addr', description: 'Brief format output' }
      ]
    }
  },
  {
    id: 'net-008',
    title: 'List Listening Ports',
    description: 'Show all TCP and UDP ports that are listening for connections.',
    task: 'Display all listening TCP and UDP ports with their numeric addresses.',
    hint: 'Use netstat with -tuln flags.',
    example: 'netstat -a',
    answer: ['netstat -tuln'],
    tips: [
      '-t TCP, -u UDP, -l listening, -n numeric addresses',
      'ss is the modern replacement for netstat'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'netstat',
      description: 'Display network connections, routing tables, and interface statistics',
      syntax: 'netstat [options]',
      options: [
        { flag: '-t', description: 'Show TCP connections' },
        { flag: '-u', description: 'Show UDP connections' },
        { flag: '-l', description: 'Show only listening sockets' },
        { flag: '-n', description: 'Show numeric addresses instead of resolving names' },
        { flag: '-p', description: 'Show process ID and name (requires sudo)' },
        { flag: '-a', description: 'Show all sockets (listening and non-listening)' }
      ],
      examples: [
        { code: 'netstat -tuln', description: 'List all listening TCP/UDP ports numerically' },
        { code: 'netstat -tulnp', description: 'Include process information' },
        { code: 'netstat -an', description: 'Show all connections numerically' }
      ]
    }
  },
  {
    id: 'net-009',
    title: 'List Listening Ports (Modern)',
    description: 'Use the ss command (socket statistics) to show listening ports.',
    task: 'Display all listening TCP and UDP ports using ss command.',
    hint: 'Use ss with -tuln flags (same as netstat).',
    example: 'ss -a',
    answer: ['ss -tuln'],
    tips: [
      'ss is faster and more feature-rich than netstat',
      '-p shows the process using each socket (requires sudo)'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'ss',
      description: 'Utility to investigate sockets - modern replacement for netstat',
      syntax: 'ss [options] [filter]',
      options: [
        { flag: '-t', description: 'Display TCP sockets' },
        { flag: '-u', description: 'Display UDP sockets' },
        { flag: '-l', description: 'Display listening sockets' },
        { flag: '-n', description: 'Do not resolve service names' },
        { flag: '-p', description: 'Show process using socket' },
        { flag: '-s', description: 'Print summary statistics' }
      ],
      examples: [
        { code: 'ss -tuln', description: 'Show listening TCP/UDP ports' },
        { code: 'ss -tulnp', description: 'Include process information' },
        { code: 'ss -s', description: 'Show socket summary statistics' }
      ]
    }
  },
  {
    id: 'net-010',
    title: 'DNS Lookup',
    description: 'Look up the IP address associated with a domain name.',
    task: 'Find the IP address of google.com using the host command.',
    hint: 'Use host followed by the domain name.',
    example: 'host example.com',
    answer: ['host google.com'],
    tips: [
      'host shows A records (IPv4) and AAAA records (IPv6)',
      'Also shows mail servers (MX records)'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'host',
      description: 'DNS lookup utility for finding IP addresses and DNS records',
      syntax: 'host [options] <name> [server]',
      options: [
        { flag: '-t <type>', description: 'Query specific record type (A, AAAA, MX, NS, etc.)' },
        { flag: '-a', description: 'Query all available record types' },
        { flag: '-v', description: 'Verbose output' },
        { flag: '-4', description: 'Use IPv4 only' },
        { flag: '-6', description: 'Use IPv6 only' }
      ],
      examples: [
        { code: 'host google.com', description: 'Look up IP address for domain' },
        { code: 'host -t MX google.com', description: 'Find mail servers' },
        { code: 'host -t NS google.com', description: 'Find name servers' }
      ]
    }
  },

  // INTERMEDIATE LESSONS (10)
  {
    id: 'net-011',
    title: 'curl POST Request',
    description: 'Send data using an HTTP POST request.',
    task: 'Send a POST request to https://httpbin.org/post with data "name=john".',
    hint: 'Use -X POST and -d for data.',
    example: 'curl -X POST -d "key=value" https://api.example.com',
    answer: [
      'curl -X POST -d "name=john" https://httpbin.org/post',
      'curl -d "name=john" https://httpbin.org/post'
    ],
    tips: [
      '-d automatically sets the method to POST',
      '-X POST is explicit but optional with -d'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'curl',
      description: 'Send HTTP POST requests with form data or other payloads',
      syntax: 'curl -X POST -d <data> <URL>',
      options: [
        { flag: '-X POST', description: 'Specify POST method (optional with -d)' },
        { flag: '-d <data>', description: 'Send data in POST request body' },
        { flag: '--data-urlencode <data>', description: 'URL-encode the data before sending' },
        { flag: '-F <field=value>', description: 'Send multipart form data' },
        { flag: '--data-binary <data>', description: 'Send data exactly as specified' }
      ],
      examples: [
        { code: 'curl -d "name=john" https://api.example.com', description: 'Simple POST with form data' },
        { code: 'curl -X POST -d "key=value&other=data" https://api.example.com', description: 'POST with multiple fields' },
        { code: 'curl -F "file=@photo.jpg" https://api.example.com/upload', description: 'Upload file via POST' }
      ]
    }
  },
  {
    id: 'net-012',
    title: 'curl Custom Headers',
    description: 'Add custom HTTP headers to your request.',
    task: 'Send a GET request to https://api.example.com with header "Authorization: Bearer token123".',
    hint: 'Use -H to add a header.',
    example: 'curl -H "Content-Type: application/json" https://api.example.com',
    answer: [
      'curl -H "Authorization: Bearer token123" https://api.example.com'
    ],
    tips: [
      'Use multiple -H flags for multiple headers',
      'Common headers: Content-Type, Accept, Authorization'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'curl',
      description: 'Add custom HTTP headers to requests for authentication and content negotiation',
      syntax: 'curl -H <header> <URL>',
      options: [
        { flag: '-H <header>', description: 'Add custom header (format: "Name: Value")' },
        { flag: '-A <agent>', description: 'Set User-Agent header' },
        { flag: '-e <referer>', description: 'Set Referer header' },
        { flag: '-b <cookies>', description: 'Send cookies with request' },
        { flag: '-c <file>', description: 'Save cookies to file' }
      ],
      examples: [
        { code: 'curl -H "Authorization: Bearer token123" https://api.example.com', description: 'Add auth header' },
        { code: 'curl -H "Accept: application/json" -H "Content-Type: application/json" https://api.example.com', description: 'Multiple headers' },
        { code: 'curl -A "MyApp/1.0" https://example.com', description: 'Set custom User-Agent' }
      ]
    }
  },
  {
    id: 'net-013',
    title: 'curl Basic Authentication',
    description: 'Send a request with HTTP Basic Authentication credentials.',
    task: 'Access https://api.example.com with username "admin" and password "secret".',
    hint: 'Use -u for user credentials.',
    example: 'curl -u user:pass https://secure.site.com',
    answer: ['curl -u admin:secret https://api.example.com'],
    tips: [
      '-u sends credentials in Basic Auth header',
      'Credentials are base64 encoded (not encrypted!)'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'curl',
      description: 'Authenticate requests using HTTP Basic Authentication',
      syntax: 'curl -u <user:password> <URL>',
      options: [
        { flag: '-u <user:pass>', description: 'Provide username and password for Basic Auth' },
        { flag: '--basic', description: 'Use HTTP Basic Authentication (default)' },
        { flag: '--digest', description: 'Use HTTP Digest Authentication' },
        { flag: '-n', description: 'Use credentials from .netrc file' },
        { flag: '--ntlm', description: 'Use NTLM authentication' }
      ],
      examples: [
        { code: 'curl -u admin:secret https://api.example.com', description: 'Basic authentication' },
        { code: 'curl -u admin https://api.example.com', description: 'Prompt for password' },
        { code: 'curl --digest -u admin:secret https://api.example.com', description: 'Digest authentication' }
      ]
    }
  },
  {
    id: 'net-014',
    title: 'netcat Listen Mode',
    description: 'Use netcat to listen for incoming connections on a port.',
    task: 'Start netcat listening on port 8080.',
    hint: 'Use nc -l with the port number.',
    example: 'nc -l 9000',
    answer: ['nc -l 8080', 'nc -l -p 8080', 'netcat -l 8080'],
    tips: [
      'Useful for testing and debugging connections',
      'Add -v for verbose output'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'nc (netcat)',
      description: 'Create a listening socket to accept incoming network connections',
      syntax: 'nc -l [options] <port>',
      options: [
        { flag: '-l', description: 'Listen mode - wait for incoming connections' },
        { flag: '-p <port>', description: 'Specify port (some versions require this)' },
        { flag: '-v', description: 'Verbose output' },
        { flag: '-k', description: 'Keep listening after client disconnects' },
        { flag: '-u', description: 'Use UDP instead of TCP' }
      ],
      examples: [
        { code: 'nc -l 8080', description: 'Listen on port 8080' },
        { code: 'nc -lv 8080', description: 'Listen with verbose output' },
        { code: 'nc -lk 8080', description: 'Keep listening for multiple connections' }
      ]
    }
  },
  {
    id: 'net-015',
    title: 'netcat Connect',
    description: 'Use netcat to connect to a server on a specific port.',
    task: 'Connect to server "myserver.com" on port 80 using netcat.',
    hint: 'Use nc hostname port.',
    example: 'nc localhost 3000',
    answer: ['nc myserver.com 80', 'netcat myserver.com 80'],
    tips: [
      'You can type HTTP requests manually after connecting',
      'Useful for testing if a port is open'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'nc (netcat)',
      description: 'Connect to a remote server on a specific port for testing and communication',
      syntax: 'nc [options] <host> <port>',
      options: [
        { flag: '-v', description: 'Verbose output - show connection details' },
        { flag: '-w <timeout>', description: 'Connection timeout in seconds' },
        { flag: '-u', description: 'Use UDP instead of TCP' },
        { flag: '-n', description: 'Do not resolve hostnames' },
        { flag: '-q <seconds>', description: 'Quit after EOF with delay' }
      ],
      examples: [
        { code: 'nc myserver.com 80', description: 'Connect to web server' },
        { code: 'nc -v myserver.com 22', description: 'Connect with verbose output' },
        { code: 'nc -w 5 myserver.com 80', description: 'Connect with 5 second timeout' }
      ]
    }
  },
  {
    id: 'net-016',
    title: 'Port Scanning with netcat',
    description: 'Scan a range of ports to check which ones are open.',
    task: 'Scan ports 20-25 on host 192.168.1.1 using netcat.',
    hint: 'Use -zv flags for scanning.',
    example: 'nc -zv localhost 80-90',
    answer: ['nc -zv 192.168.1.1 20-25'],
    tips: [
      '-z means zero-I/O mode (scanning)',
      '-v provides verbose output'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'nc (netcat)',
      description: 'Scan ports to discover which services are running on a host',
      syntax: 'nc -zv <host> <port-range>',
      options: [
        { flag: '-z', description: 'Zero-I/O mode - just scan, do not send data' },
        { flag: '-v', description: 'Verbose - report open/closed status' },
        { flag: '-w <timeout>', description: 'Timeout for each connection attempt' },
        { flag: '-n', description: 'Skip DNS resolution for faster scanning' },
        { flag: '-u', description: 'Scan UDP ports instead of TCP' }
      ],
      examples: [
        { code: 'nc -zv 192.168.1.1 20-25', description: 'Scan port range' },
        { code: 'nc -zv -w 1 host.com 22 80 443', description: 'Scan specific ports with timeout' },
        { code: 'nc -zvu host.com 53', description: 'Scan UDP port 53 (DNS)' }
      ]
    }
  },
  {
    id: 'net-017',
    title: 'Trace Network Route',
    description: 'Show the route packets take to reach a destination.',
    task: 'Trace the route to google.com.',
    hint: 'Use traceroute command.',
    example: 'traceroute example.com',
    answer: ['traceroute google.com'],
    tips: [
      'Shows each hop between you and the destination',
      'Useful for diagnosing network path issues'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'traceroute',
      description: 'Display the route packets take to reach a network host',
      syntax: 'traceroute [options] <host>',
      options: [
        { flag: '-n', description: 'Do not resolve IP addresses to hostnames' },
        { flag: '-m <max_hops>', description: 'Set maximum number of hops (default 30)' },
        { flag: '-w <timeout>', description: 'Wait time for response in seconds' },
        { flag: '-q <queries>', description: 'Number of queries per hop' },
        { flag: '-I', description: 'Use ICMP ECHO instead of UDP' }
      ],
      examples: [
        { code: 'traceroute google.com', description: 'Trace route to destination' },
        { code: 'traceroute -n google.com', description: 'Trace without DNS resolution' },
        { code: 'traceroute -m 15 google.com', description: 'Limit to 15 hops' }
      ]
    }
  },
  {
    id: 'net-018',
    title: 'Advanced DNS Lookup',
    description: 'Use nslookup for more detailed DNS queries.',
    task: 'Look up DNS records for example.com using nslookup.',
    hint: 'Use nslookup followed by the domain.',
    example: 'nslookup google.com',
    answer: ['nslookup example.com'],
    tips: [
      'Shows the DNS server used and resolved addresses',
      'Can query specific record types'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'nslookup',
      description: 'Query DNS servers interactively or non-interactively',
      syntax: 'nslookup [options] <host> [server]',
      options: [
        { flag: '-type=<type>', description: 'Query specific record type (A, MX, NS, etc.)' },
        { flag: '-debug', description: 'Show detailed debugging information' },
        { flag: '-timeout=<seconds>', description: 'Set query timeout' },
        { flag: '-retry=<count>', description: 'Number of retries' },
        { flag: '-port=<port>', description: 'Use non-standard DNS port' }
      ],
      examples: [
        { code: 'nslookup example.com', description: 'Basic DNS lookup' },
        { code: 'nslookup -type=MX example.com', description: 'Look up mail servers' },
        { code: 'nslookup example.com 8.8.8.8', description: 'Query specific DNS server' }
      ]
    }
  },
  {
    id: 'net-019',
    title: 'DNS dig Command',
    description: 'Use dig for powerful DNS lookups with detailed output.',
    task: 'Query DNS records for github.com using dig.',
    hint: 'Use dig followed by the domain.',
    example: 'dig example.com',
    answer: ['dig github.com'],
    tips: [
      'dig shows TTL, record type, and response time',
      'Add +short for concise output'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'dig',
      description: 'DNS lookup utility with detailed output and flexible querying',
      syntax: 'dig [@server] <name> [type] [options]',
      options: [
        { flag: '+short', description: 'Display only the answer, no extra info' },
        { flag: '+trace', description: 'Trace delegation path from root servers' },
        { flag: '+noall +answer', description: 'Show only the answer section' },
        { flag: '-x <IP>', description: 'Reverse DNS lookup' },
        { flag: '@<server>', description: 'Query specific DNS server' }
      ],
      examples: [
        { code: 'dig github.com', description: 'Full DNS lookup with details' },
        { code: 'dig +short github.com', description: 'Get just the IP address' },
        { code: 'dig github.com MX', description: 'Query mail server records' },
        { code: 'dig -x 8.8.8.8', description: 'Reverse lookup an IP' }
      ]
    }
  },
  {
    id: 'net-020',
    title: 'Recursive Download with wget',
    description: 'Download an entire website or directory recursively.',
    task: 'Recursively download all files from https://example.com/docs/.',
    hint: 'Use wget with -r flag for recursive.',
    example: 'wget -r https://somesite.com/files/',
    answer: ['wget -r https://example.com/docs/'],
    tips: [
      '-r enables recursive downloading',
      '-l specifies the depth level'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'wget',
      description: 'Download files from the web with recursive and mirroring capabilities',
      syntax: 'wget [options] <URL>',
      options: [
        { flag: '-r', description: 'Recursive download - follow links' },
        { flag: '-l <depth>', description: 'Maximum recursion depth (0 = infinite)' },
        { flag: '-np', description: 'Do not ascend to parent directory' },
        { flag: '-k', description: 'Convert links for local viewing' },
        { flag: '-p', description: 'Download all page requisites (images, CSS, etc.)' },
        { flag: '-c', description: 'Continue partially downloaded files' }
      ],
      examples: [
        { code: 'wget -r https://example.com/docs/', description: 'Recursive download' },
        { code: 'wget -r -l 2 https://example.com/', description: 'Download 2 levels deep' },
        { code: 'wget -r -np -k https://example.com/docs/', description: 'Mirror for offline viewing' }
      ]
    }
  },

  // ADVANCED LESSONS (5)
  {
    id: 'net-021',
    title: 'curl JSON POST',
    description: 'Send a JSON payload in a POST request.',
    task: 'POST JSON data {"name":"john","age":30} to https://api.example.com/users with proper Content-Type header.',
    hint: 'Use -H for Content-Type and -d for the JSON data.',
    example: 'curl -X POST -H "Content-Type: application/json" -d \'{"key":"value"}\' https://api.example.com',
    answer: [
      'curl -X POST -H "Content-Type: application/json" -d \'{"name":"john","age":30}\' https://api.example.com/users',
      'curl -H "Content-Type: application/json" -d \'{"name":"john","age":30}\' https://api.example.com/users'
    ],
    tips: [
      'Always set Content-Type: application/json for JSON',
      'Use single quotes around JSON to avoid escaping'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'curl',
      description: 'Send JSON data in API requests with proper headers',
      syntax: 'curl -H "Content-Type: application/json" -d \'<json>\' <URL>',
      options: [
        { flag: '-H "Content-Type: application/json"', description: 'Set JSON content type header' },
        { flag: '-d \'<json>\'', description: 'JSON payload to send' },
        { flag: '--json \'<json>\'', description: 'Shorthand for JSON POST (curl 7.82+)' },
        { flag: '-X POST', description: 'Explicit POST method (optional with -d)' },
        { flag: '@<file>', description: 'Read JSON from file: -d @data.json' }
      ],
      examples: [
        { code: 'curl -H "Content-Type: application/json" -d \'{"name":"john"}\' https://api.example.com', description: 'POST JSON data' },
        { code: 'curl --json \'{"key":"value"}\' https://api.example.com', description: 'Using --json shorthand' },
        { code: 'curl -H "Content-Type: application/json" -d @data.json https://api.example.com', description: 'POST JSON from file' }
      ]
    }
  },
  {
    id: 'net-022',
    title: 'netcat File Transfer',
    description: 'Transfer a file over the network using netcat.',
    task: 'On the receiving end, listen on port 9999 and save incoming data to "received.txt".',
    hint: 'Use nc -l with output redirection.',
    example: 'nc -l 8000 > output.file',
    answer: ['nc -l 9999 > received.txt', 'nc -l -p 9999 > received.txt'],
    tips: [
      'Sender would use: nc hostname 9999 < file.txt',
      'Useful for transferring files without scp/sftp'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'nc (netcat)',
      description: 'Transfer files over the network using shell redirection',
      syntax: 'nc -l <port> > <file>  (receiver) | nc <host> <port> < <file>  (sender)',
      options: [
        { flag: '-l <port>', description: 'Listen on port for incoming connection' },
        { flag: '> <file>', description: 'Redirect received data to file' },
        { flag: '< <file>', description: 'Send file contents to connection' },
        { flag: '-q <seconds>', description: 'Quit after EOF with delay' },
        { flag: '-v', description: 'Verbose mode for connection status' }
      ],
      examples: [
        { code: 'nc -l 9999 > received.txt', description: 'Receive file on port 9999' },
        { code: 'nc host.com 9999 < myfile.txt', description: 'Send file to receiver' },
        { code: 'tar czf - /dir | nc host.com 9999', description: 'Transfer compressed directory' }
      ]
    }
  },
  {
    id: 'net-023',
    title: 'Basic tcpdump',
    description: 'Capture network traffic on an interface.',
    task: 'Capture packets on interface eth0.',
    hint: 'Use tcpdump with -i flag.',
    example: 'tcpdump -i lo',
    answer: ['tcpdump -i eth0', 'sudo tcpdump -i eth0'],
    tips: [
      'Requires root/sudo privileges',
      '-c N captures only N packets'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'tcpdump',
      description: 'Capture and analyze network packets on an interface',
      syntax: 'tcpdump [options] [filter expression]',
      options: [
        { flag: '-i <interface>', description: 'Capture on specific interface' },
        { flag: '-c <count>', description: 'Capture only N packets then stop' },
        { flag: '-w <file>', description: 'Write packets to file (pcap format)' },
        { flag: '-r <file>', description: 'Read packets from file' },
        { flag: '-n', description: 'Do not resolve hostnames' },
        { flag: '-v/-vv/-vvv', description: 'Increase verbosity level' }
      ],
      examples: [
        { code: 'tcpdump -i eth0', description: 'Capture on eth0 interface' },
        { code: 'tcpdump -i eth0 -c 100', description: 'Capture only 100 packets' },
        { code: 'tcpdump -i eth0 -w capture.pcap', description: 'Save capture to file' }
      ]
    }
  },
  {
    id: 'net-024',
    title: 'View Firewall Rules',
    description: 'Display the current iptables firewall rules.',
    task: 'List all iptables firewall rules.',
    hint: 'Use iptables with -L flag.',
    example: 'iptables -S',
    answer: ['iptables -L', 'sudo iptables -L', 'iptables -L -n'],
    tips: [
      '-L lists all rules in all chains',
      '-n shows numeric addresses (faster)'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'iptables',
      description: 'Administration tool for IPv4 packet filtering and NAT',
      syntax: 'iptables [options] [chain] [rule-specification]',
      options: [
        { flag: '-L', description: 'List all rules in all chains' },
        { flag: '-n', description: 'Numeric output (no DNS resolution)' },
        { flag: '-v', description: 'Verbose output with packet counts' },
        { flag: '--line-numbers', description: 'Show rule line numbers' },
        { flag: '-S', description: 'Print all rules in iptables-save format' },
        { flag: '-t <table>', description: 'Specify table (filter, nat, mangle)' }
      ],
      examples: [
        { code: 'iptables -L', description: 'List all firewall rules' },
        { code: 'iptables -L -n -v', description: 'Verbose list with numeric addresses' },
        { code: 'iptables -L INPUT', description: 'List only INPUT chain rules' },
        { code: 'iptables -S', description: 'Show rules in save format' }
      ]
    }
  },
  {
    id: 'net-025',
    title: 'Find Process Using Port',
    description: 'Find which process is using a specific network port.',
    task: 'Find the process listening on port 80.',
    hint: 'Use netstat or ss with grep.',
    example: 'netstat -tuln | grep 443',
    answer: [
      'netstat -tulnp | grep :80',
      'ss -tulnp | grep :80',
      'lsof -i :80'
    ],
    tips: [
      '-p shows the process ID (requires sudo)',
      'lsof -i :port is another powerful option'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'lsof',
      description: 'List open files and network connections by process',
      syntax: 'lsof -i [protocol][@host][:port]',
      options: [
        { flag: '-i', description: 'List network connections' },
        { flag: '-i :<port>', description: 'Show processes using specific port' },
        { flag: '-i TCP', description: 'Show only TCP connections' },
        { flag: '-i UDP', description: 'Show only UDP connections' },
        { flag: '-P', description: 'Show port numbers instead of names' },
        { flag: '-n', description: 'Do not resolve hostnames' }
      ],
      examples: [
        { code: 'lsof -i :80', description: 'Find process using port 80' },
        { code: 'lsof -i TCP:22', description: 'Find SSH connections' },
        { code: 'lsof -i -P -n', description: 'List all network connections' },
        { code: 'ss -tulnp | grep :80', description: 'Alternative using ss' }
      ]
    }
  },

  // EXPERT LESSONS (5)
  {
    id: 'net-026',
    title: 'curl Verbose SSL Debug',
    description: 'Debug SSL/TLS connections with verbose output.',
    task: 'Make a request to https://example.com with verbose output showing SSL handshake details.',
    hint: 'Use -v for verbose output.',
    example: 'curl -v https://google.com',
    answer: ['curl -v https://example.com'],
    tips: [
      '-v shows SSL certificate info and handshake',
      '--trace-ascii file.txt for even more detail'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'curl',
      description: 'Debug SSL/TLS connections and inspect certificate details',
      syntax: 'curl -v <URL>',
      options: [
        { flag: '-v', description: 'Verbose mode showing SSL handshake' },
        { flag: '--trace <file>', description: 'Full trace dump to file' },
        { flag: '--trace-ascii <file>', description: 'Trace dump without hex' },
        { flag: '-k', description: 'Allow insecure SSL connections' },
        { flag: '--cert <file>', description: 'Use client certificate' },
        { flag: '--cacert <file>', description: 'Use custom CA certificate' }
      ],
      examples: [
        { code: 'curl -v https://example.com', description: 'Verbose SSL connection' },
        { code: 'curl --trace-ascii debug.txt https://example.com', description: 'Full debug trace' },
        { code: 'curl -v --cert client.pem https://secure.example.com', description: 'With client cert' }
      ]
    }
  },
  {
    id: 'net-027',
    title: 'tcpdump with Filters',
    description: 'Capture only specific traffic using tcpdump filters.',
    task: 'Capture only HTTP traffic (port 80) on interface eth0.',
    hint: 'Use port filter expression.',
    example: 'tcpdump -i eth0 host google.com',
    answer: [
      'tcpdump -i eth0 port 80',
      'sudo tcpdump -i eth0 port 80'
    ],
    tips: [
      'Filters: port, host, src, dst, tcp, udp',
      'Combine with and/or: port 80 or port 443'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'tcpdump',
      description: 'Filter network captures using BPF (Berkeley Packet Filter) expressions',
      syntax: 'tcpdump -i <interface> <filter expression>',
      options: [
        { flag: 'port <num>', description: 'Filter by port number' },
        { flag: 'host <addr>', description: 'Filter by host IP or name' },
        { flag: 'src/dst', description: 'Filter by source or destination' },
        { flag: 'tcp/udp/icmp', description: 'Filter by protocol' },
        { flag: 'and/or/not', description: 'Combine filter expressions' },
        { flag: '-A', description: 'Print packet contents in ASCII' }
      ],
      examples: [
        { code: 'tcpdump -i eth0 port 80', description: 'Capture HTTP traffic' },
        { code: 'tcpdump -i eth0 host 192.168.1.1', description: 'Capture traffic to/from host' },
        { code: 'tcpdump -i eth0 port 80 or port 443', description: 'HTTP and HTTPS traffic' },
        { code: 'tcpdump -i eth0 -A port 80', description: 'Show HTTP content in ASCII' }
      ]
    }
  },
  {
    id: 'net-028',
    title: 'curl with Proxy',
    description: 'Route curl requests through a proxy server.',
    task: 'Make a request to https://example.com through proxy http://proxy.company.com:8080.',
    hint: 'Use -x or --proxy flag.',
    example: 'curl -x http://proxy:port https://target.com',
    answer: ['curl -x http://proxy.company.com:8080 https://example.com'],
    tips: [
      '-x sets the proxy URL',
      'Supports HTTP, HTTPS, SOCKS4, SOCKS5 proxies'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'curl',
      description: 'Route requests through proxy servers for privacy or corporate networks',
      syntax: 'curl -x <proxy_url> <URL>',
      options: [
        { flag: '-x <proxy>', description: 'Use proxy server (http, https, socks4, socks5)' },
        { flag: '--proxy-user <user:pass>', description: 'Proxy authentication' },
        { flag: '--noproxy <hosts>', description: 'Hosts to bypass proxy' },
        { flag: '--socks5 <host:port>', description: 'Use SOCKS5 proxy' },
        { flag: '--proxy-insecure', description: 'Skip proxy SSL verification' }
      ],
      examples: [
        { code: 'curl -x http://proxy.company.com:8080 https://example.com', description: 'HTTP proxy' },
        { code: 'curl --socks5 localhost:9050 https://example.com', description: 'SOCKS5 proxy (Tor)' },
        { code: 'curl -x http://proxy:8080 --proxy-user user:pass https://example.com', description: 'Authenticated proxy' }
      ]
    }
  },
  {
    id: 'net-029',
    title: 'Test HTTP Methods',
    description: 'Test different HTTP methods on an API endpoint.',
    task: 'Send a DELETE request to https://api.example.com/users/123.',
    hint: 'Use -X to specify the HTTP method.',
    example: 'curl -X PUT https://api.example.com/resource',
    answer: ['curl -X DELETE https://api.example.com/users/123'],
    tips: [
      'Common methods: GET, POST, PUT, PATCH, DELETE',
      'Some APIs require authentication for DELETE'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'curl',
      description: 'Test RESTful APIs using different HTTP methods',
      syntax: 'curl -X <METHOD> <URL>',
      options: [
        { flag: '-X <method>', description: 'Specify HTTP method (GET, POST, PUT, PATCH, DELETE)' },
        { flag: '-I', description: 'HEAD request (headers only)' },
        { flag: '-G', description: 'Send data as GET query string' },
        { flag: '--request-target <path>', description: 'Specify request target' },
        { flag: '-d <data>', description: 'Include request body' }
      ],
      examples: [
        { code: 'curl -X DELETE https://api.example.com/users/123', description: 'DELETE request' },
        { code: 'curl -X PUT -d \'{"name":"new"}\' -H "Content-Type: application/json" https://api.example.com/users/123', description: 'PUT with JSON body' },
        { code: 'curl -X PATCH -d \'{"status":"active"}\' https://api.example.com/users/123', description: 'PATCH request' }
      ]
    }
  },
  {
    id: 'net-030',
    title: 'curl Timing Analysis',
    description: 'Measure and analyze request timing for performance debugging.',
    task: 'Make a request to https://example.com and display connection timing details.',
    hint: 'Use -w with timing variables.',
    example: 'curl -w "Total: %{time_total}s\\n" https://example.com',
    answer: [
      'curl -w "DNS: %{time_namelookup}s\\nConnect: %{time_connect}s\\nTotal: %{time_total}s\\n" -o /dev/null -s https://example.com',
      'curl -w "%{time_total}\\n" -o /dev/null -s https://example.com'
    ],
    tips: [
      '-w writes out specified format after completion',
      'Available: time_namelookup, time_connect, time_total'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'curl',
      description: 'Analyze request performance with detailed timing metrics',
      syntax: 'curl -w "<format>" -o /dev/null -s <URL>',
      options: [
        { flag: '-w <format>', description: 'Write out timing info after transfer' },
        { flag: '%{time_namelookup}', description: 'DNS resolution time' },
        { flag: '%{time_connect}', description: 'TCP connection time' },
        { flag: '%{time_appconnect}', description: 'SSL/TLS handshake time' },
        { flag: '%{time_starttransfer}', description: 'Time to first byte' },
        { flag: '%{time_total}', description: 'Total request time' }
      ],
      examples: [
        { code: 'curl -w "%{time_total}\\n" -o /dev/null -s https://example.com', description: 'Show total time only' },
        { code: 'curl -w "DNS: %{time_namelookup}s\\nConnect: %{time_connect}s\\nSSL: %{time_appconnect}s\\nTotal: %{time_total}s\\n" -o /dev/null -s https://example.com', description: 'Detailed timing breakdown' },
        { code: 'curl -w "@curl-format.txt" -o /dev/null -s https://example.com', description: 'Use format file' }
      ]
    }
  }
];

export default networkingLessons;
